import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { productAPI, categoryAPI } from '../api/services';
import ProductCard from '../components/product/ProductCard';
import Loading from '../components/common/Loading';
import './Home.css';

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [kidsProducts, setKidsProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);

  useEffect(() => {
    Promise.all([
      productAPI.getFeatured(0),
      productAPI.getAll(0, 8),
      categoryAPI.getAll(),
    ]).then(([featuredRes, newRes, catRes]) => {
      setFeatured(featuredRes.data.data.content || []);
      setNewArrivals(newRes.data.data.content || []);
      const cats = catRes.data.data || [];
      setCategories(cats);

      // Find kids category and load its products for the slider
      const kidsCat = cats.find(c => c.name.toLowerCase().includes('kids'));
      if (kidsCat) {
        productAPI.getByCategory(kidsCat.id, 0)
          .then(res => setKidsProducts(res.data.data.content || []))
          .catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const scrollSlider = useCallback((direction) => {
    if (!sliderRef.current) return;
    const cardWidth = sliderRef.current.querySelector('.slider-card')?.offsetWidth || 260;
    const gap = 20;
    const scrollAmount = (cardWidth + gap) * 2;
    sliderRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (kidsProducts.length === 0) return;
    autoPlayRef.current = setInterval(() => {
      if (!sliderRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      if (scrollLeft + clientWidth >= scrollWidth - 10) {
        sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollSlider('right');
      }
    }, 4000);
    return () => clearInterval(autoPlayRef.current);
  }, [kidsProducts, scrollSlider]);

  const [heroSlide, setHeroSlide] = useState(0);
  const heroAutoRef = useRef(null);

  // Auto-rotate hero slider — use kids products, fallback to featured or newArrivals
  const heroSource = kidsProducts.length > 0 ? kidsProducts : featured.length > 0 ? featured : newArrivals;
  const heroProducts = heroSource.slice(0, 5);

  useEffect(() => {
    if (heroSource.length === 0) return;
    heroAutoRef.current = setInterval(() => {
      setHeroSlide(prev => (prev + 1) % Math.min(heroSource.length, 5));
    }, 3500);
    return () => clearInterval(heroAutoRef.current);
  }, [heroSource]);

  if (loading) return <Loading />;

  return (
    <div className="home">
      <section className="hero"
        onMouseEnter={() => clearInterval(heroAutoRef.current)}
        onMouseLeave={() => {
          if (heroProducts.length > 0) {
            heroAutoRef.current = setInterval(() => {
              setHeroSlide(prev => (prev + 1) % heroProducts.length);
            }, 3500);
          }
        }}
      >
        {heroProducts.length > 0 && (
          <div className="hero-slider">
            {heroProducts.map((p, i) => {
              const img = p.images?.find(i => i.isPrimary) || p.images?.[0];
              return img ? (
                <Link key={p.id} to={`/product/${p.slug}`}
                  className={`hero-slide ${i === heroSlide ? 'active' : ''}`}
                >
                  <img src={img.imageUrl} alt={p.name} />
                </Link>
              ) : null;
            })}
            <div className="hero-dots">
              {heroProducts.map((_, i) => (
                <button key={i}
                  className={`hero-dot ${i === heroSlide ? 'active' : ''}`}
                  onClick={() => setHeroSlide(i)}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
            <button className="hero-nav hero-nav-left" onClick={() => setHeroSlide(prev => (prev - 1 + heroProducts.length) % heroProducts.length)} aria-label="Previous slide">
              <FiChevronLeft />
            </button>
            <button className="hero-nav hero-nav-right" onClick={() => setHeroSlide(prev => (prev + 1) % heroProducts.length)} aria-label="Next slide">
              <FiChevronRight />
            </button>
          </div>
        )}
        <div className="hero-content">
          <h1>New Season Arrivals</h1>
          <p>Discover the latest trends in kids fashion. Premium quality at affordable prices.</p>
          <Link to="/shop" className="btn btn-primary btn-lg">Shop Now</Link>
        </div>
      </section>

      {categories.length > 0 && (
        <section className="section">
          <h2 className="section-title">Shop by Category</h2>
          <div className="category-grid">
            {categories.slice(0, 6).map(cat => (
              <Link key={cat.id} to={`/shop?categoryId=${cat.id}`} className="category-card">
                {cat.imageUrl ? (
                  <img src={cat.imageUrl} alt={cat.name} />
                ) : (
                  <div className="category-placeholder">{cat.name.charAt(0)}</div>
                )}
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {kidsProducts.length > 0 && (
        <section className="section kids-slider-section">
          <div className="section-header">
            <div>
              <h2 className="section-title">Kids Collection 🧸</h2>
              <p className="section-subtitle">Adorable styles for your little ones</p>
            </div>
            <Link to={`/shop?categoryId=${categories.find(c => c.name.toLowerCase().includes('kids'))?.id || ''}`} className="section-link">View All →</Link>
          </div>
          <div className="slider-wrapper"
            onMouseEnter={() => clearInterval(autoPlayRef.current)}
            onMouseLeave={() => {
              autoPlayRef.current = setInterval(() => {
                if (!sliderRef.current) return;
                const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
                if (scrollLeft + clientWidth >= scrollWidth - 10) {
                  sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                  scrollSlider('right');
                }
              }, 4000);
            }}
          >
            <button className="slider-btn slider-btn-left" onClick={() => scrollSlider('left')} aria-label="Scroll left">
              <FiChevronLeft />
            </button>
            <div className="slider-track" ref={sliderRef}>
              {kidsProducts.map(p => (
                <div key={p.id} className="slider-card">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
            <button className="slider-btn slider-btn-right" onClick={() => scrollSlider('right')} aria-label="Scroll right">
              <FiChevronRight />
            </button>
          </div>
        </section>
      )}

      {featured.length > 0 && (
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">Featured Products</h2>
            <Link to="/shop?featured=true" className="section-link">View All →</Link>
          </div>
          <div className="product-grid">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-header">
          <h2 className="section-title">New Arrivals</h2>
          <Link to="/shop" className="section-link">View All →</Link>
        </div>
        <div className="product-grid">
          {newArrivals.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      <section className="promo-banner">
        <h2>Free Shipping on Orders Over ₹999</h2>
        <p>Shop your favorite styles with free delivery.</p>
        <Link to="/shop" className="btn btn-outline btn-lg">Start Shopping</Link>
      </section>
    </div>
  );
}
